import React, { useEffect, useMemo, useState, JSX, useCallback } from 'react';
import moment from 'moment';
import { getCoreRowModel } from '@tanstack/react-table';

// MUI Theme
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { MenuItem, Select, Tab } from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import Paper from '@mui/material/Paper';

// Hooks
import { useAppDispatch, useAppSelector } from '../../../redux/hook';

// Utils
import { useExport } from '../../../hooks/useExport';
import { makeReaderListColumn } from '../../../utils/columns/makeColumn';
import { Storage, getRetainState } from '../../../utils/Storage';
import { FormatForApi, shortDateFormat } from '../../../utils/Format';

// Services
import { exportReaders, getReaders } from '../../../services/reader.service';

// Redux
import { fetchAllSettings } from '../../../redux/slices/settingsSlice';

// Types
import { ReaderOwnerAccountId, SearchBarFilterTypes } from '../../../constants/Constants';
import { FilterableTypes } from '../../../types/FilterableTypes';
import { Reader } from '../../../types/Reader';

// Components
import { Table, useNewTable } from '../../common/table/index';
import SearchBar from '../../common/SearchBar/SearchBar';
import ReadersMapView from './components/ReadersMapView';
import FormInputCollection from '../../common/FormInputCollection/FormInputCollection';

// Styles
import '../../../styles/muiTabs.scss';
interface ReaderProps {
  accessKey?: string;
}

const ReaderOwnerOptions = Object.entries(ReaderOwnerAccountId).map(([label, value]) => ({
  label: label.replace(/([a-z])([A-Z])/g, '$1 $2'),
  value,
}));

export default function Readers(props: ReaderProps): JSX.Element {
  const dispatch = useAppDispatch();
  const settingStates = useAppSelector((state) => state.settings);
  const [supplyChainStatus, setSupplyChainStatus] = useState<number | undefined>();
  const [opsStatus, setOpsStatus] = useState<number | undefined>();
  const [readerOwnership, setReaderOwnership] = useState<string | undefined>();
  const [isMobile, setIsMobile] = useState<number | undefined>();
  const [lastSeenDateTimeFrom, setLastSeenDateTimeFrom] = useState<Date | null>(null);
  const [lastSeenDateTimeTo, setLastSeenDateTimeTo] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState('1');

  const [paramFilter, setParamFilter] = useState<any>({
    entityType: 'Reader',
    sortBy: [{ lastSeenDateTime: '-1' }],
  });

  const { isExporting, startExport } = useExport('Reader');

  const intialDefaultFilters = () => {
    const filterState = Storage.getSearchBarFiltering(SearchBarFilterTypes.READERS);
    if (filterState) {
      getRetainState(SearchBarFilterTypes.READERS, 'supplyChainStatus', setSupplyChainStatus);
      getRetainState(SearchBarFilterTypes.READERS, 'opsStatus', setOpsStatus);
      getRetainState(SearchBarFilterTypes.READERS, 'readerOwnership', setReaderOwnership);
      getRetainState(SearchBarFilterTypes.READERS, 'isMobile', setIsMobile);
      getRetainState(SearchBarFilterTypes.READERS, 'lastSeenDateTime', setLastSeenDateTimeFrom, true, FilterableTypes.GreaterThanOrEqual);
      getRetainState(SearchBarFilterTypes.READERS, 'lastSeenDateTime', setLastSeenDateTimeTo, true, FilterableTypes.LessThanOrEqual);

      setParamFilter({
        filters: filterState.filters,
        sortBy: paramFilter.sortBy,
      });
    }
  };

  useEffect(() => {
    dispatch(fetchAllSettings());
    intialDefaultFilters();
  }, []);

  const columns = useMemo(() => makeReaderListColumn(settingStates), [settingStates]);
  const table = useNewTable<Reader>({
    columns,
    accessKey: props.accessKey,
    fetchAsync: getReaders,
    exportAsync: exportReaders,
    params: paramFilter,
    serverSide: true,
  });

  const onSearchChange = (filters: any[]) => {
    const finalFilters = [...filters];

    if (supplyChainStatus !== undefined) {
      finalFilters.push({
        supplyChainStatus: supplyChainStatus,
        filterType: FilterableTypes.Equals,
      });
    }

    if (opsStatus !== undefined) {
      finalFilters.push({
        opsStatus: opsStatus,
        filterType: FilterableTypes.Equals,
      });
    }

    if (readerOwnership !== undefined) {
      finalFilters.push({
        accountId: readerOwnership,
        filterType: FilterableTypes.Equals,
      });
    }

    if (isMobile !== undefined) {
      finalFilters.push({
        isMobile: isMobile,
        filterType: FilterableTypes.Equals,
      });
    }

    if (lastSeenDateTimeFrom && lastSeenDateTimeTo) {
      finalFilters.push({
        lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeFrom), true),
        filterType: FilterableTypes.GreaterThanOrEqual,
        and: {
          lastSeenDateTime: FormatForApi(moment(lastSeenDateTimeTo), false),
          filterType: FilterableTypes.LessThanOrEqual,
        },
      });
    }

    setParamFilter({ entityType: 'Reader', filters: finalFilters, sortBy: paramFilter.sortBy });
    Storage.setSearchBarFiltering(SearchBarFilterTypes.READERS, { isMoreFilter: true, filters: finalFilters });
  };

  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: string) => setActiveTab(newValue), []);

  return (
    <React.Fragment>
      <TabContext value={activeTab}>
        <div className='app-tabs'>
          <TabList onChange={handleTabChange}>
            <Tab label='List View' value='1' sx={{ textTransform: 'none' }} />
            <Tab label='Map View' value='2' sx={{ textTransform: 'none' }} />
          </TabList>
        </div>
        <TabPanel value='1' style={{ display: 'contents' }}>
          <SearchBar
            pageKey={SearchBarFilterTypes.READERS}
            onSearch={onSearchChange}
            onReset={() => {
              setSupplyChainStatus(undefined);
              setOpsStatus(undefined);
              setReaderOwnership(undefined);
              setIsMobile(undefined);
              setLastSeenDateTimeFrom(null);
              setLastSeenDateTimeTo(null);
              setParamFilter({ entityType: 'Reader', filters: [] });
            }}
            columns={columns}
            allowSearchAll={true}
            components={
              <div style={{ display: 'flex', marginBottom: 5, marginLeft: '-22px', flexWrap: 'wrap' }}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <FormInputCollection label='Supply Chain' style={{ marginLeft: '24px', marginBottom: '12px', width: '130px' }}>
                    <Select
                      value={supplyChainStatus ?? 'all'}
                      variant='outlined'
                      id='supplyChainStatus'
                      name='supplyChainStatus'
                      size='small'
                      style={{ width: '130px' }}
                      onChange={(e: any) => {
                        setSupplyChainStatus(e.target.value === 'all' ? undefined : e.target.value);
                      }}
                    >
                      <MenuItem value={'all'}>All</MenuItem>
                      {settingStates.readerSupplyChainStatus?.value.map((val, index: number) => {
                        return (
                          <MenuItem key={index} value={val.text}>
                            {val.value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormInputCollection>
                  <FormInputCollection label='Ops Status' style={{ marginLeft: '24px', width: '140px' }}>
                    <Select
                      value={opsStatus ?? 'all'}
                      variant='outlined'
                      id='opsStatus'
                      name='opsStatus'
                      size='small'
                      style={{ width: '140px' }}
                      onChange={(e: any) => {
                        setOpsStatus(e.target.value === 'all' ? undefined : e.target.value);
                      }}
                    >
                      <MenuItem value={'all'}>All</MenuItem>
                      {settingStates.readerOpsStatus?.value.map((val, index: number) => {
                        return (
                          <MenuItem key={index} value={val.text}>
                            {val.value}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormInputCollection>
                  <FormInputCollection label='Mobile' style={{ marginLeft: '24px', width: '120px' }}>
                    <Select
                      value={isMobile ?? 'all'}
                      variant='outlined'
                      id='isMobile'
                      name='isMobile'
                      size='small'
                      style={{ width: '110px' }}
                      onChange={(e: any) => {
                        setIsMobile(e.target.value === 'all' ? undefined : e.target.value);
                      }}
                    >
                      <MenuItem value={'all'}>All</MenuItem>
                      <MenuItem value={1}>Yes</MenuItem>
                      <MenuItem value={0}>No</MenuItem>
                    </Select>
                  </FormInputCollection>
                  <FormInputCollection label='Ownership' style={{ marginLeft: '24px', width: '140px' }}>
                    <Select
                      value={readerOwnership ?? 'all'}
                      variant='outlined'
                      id='readerOwnership'
                      name='readerOwnership'
                      size='small'
                      style={{ width: '140px' }}
                      onChange={(e) => {
                        const selectedValue = e.target.value;
                        setReaderOwnership(selectedValue === 'all' ? undefined : selectedValue);
                      }}
                    >
                      <MenuItem value='all'>All</MenuItem>
                      {ReaderOwnerOptions.map(({ label, value }) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormInputCollection>

                  <FormInputCollection label='Last Seen date' style={{ marginLeft: '24px' }}>
                    <div style={{ marginRight: '8px' }}>
                      <DatePicker
                        value={lastSeenDateTimeFrom ? moment(lastSeenDateTimeFrom) : null}
                        label='From'
                        format={shortDateFormat}
                        sx={{ width: '185px' }}
                        slotProps={{ textField: { size: 'small' } }}
                        onChange={(date) => {
                          setLastSeenDateTimeFrom(date ? date.toDate() : null);
                        }}
                      />
                    </div>
                    <DatePicker
                      value={lastSeenDateTimeTo ? moment(lastSeenDateTimeTo) : null}
                      label='To'
                      format={shortDateFormat}
                      sx={{ width: '185px' }}
                      slotProps={{ textField: { size: 'small' } }}
                      onChange={(date) => {
                        setLastSeenDateTimeTo(date ? date.toDate() : null);
                      }}
                    />
                  </FormInputCollection>
                </LocalizationProvider>
              </div>
            }
          />
          <Paper>
            <Table
              {...table}
              isExporting={isExporting}
              canExportData={() => true}
              exportData={() => startExport(paramFilter.filters ?? [])}
              showSearchBar={false}
              showColumnToggler={false}
              getCoreRowModel={getCoreRowModel()}
            />
          </Paper>
        </TabPanel>
        <TabPanel value='2' style={{ display: 'contents' }}>
          <ReadersMapView />
        </TabPanel>
      </TabContext>
    </React.Fragment>
  );
}
